/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Asset,
  AssetMetadataImage,
  AssetMetadataLidar,
  AssetServiceClient,
  AssetSet,
  AssetSetMetadataImages,
  AssetWithPresignedUrl,
  MAX_IMAGE_DIMENSION,
} from '@droidmap/asset-service-contract'
import {
  AssetCategoryKey,
  AssetSetType,
  AssetType,
  DroidMapMetadata,
  StorageAndAssetCountDelta,
} from '@droidmap/shared-service-contract'
import {
  DroidMapError,
  ensureError,
  getTiffType,
  isMacOSMetadataFile,
} from '@droidmap/shared-utils'

/**
 * What kind of content an asset *is*, independent of which set holds it.
 *
 * Deliberately a separate classification from {@link RASTER_ASSET_TYPES},
 * which answers a different question — "can this carry EXIF and a thumbnail"
 * — and therefore counts a JPEG as a raster while this counts it as imagery.
 * Collapsing the two would either make photographs map layers or stop
 * elevation rasters getting thumbnails; droidmap#2495 is what that looked like
 * the last time these lists drifted.
 *
 * Facets compose rather than discriminate: an `odm_outputs` set legitimately
 * holds `raster` + `pointCloud` + `model` at once, which is why
 * `contained_asset_types` is an array. See the design note on droidmap#2766.
 */
export type AssetFacet =
  | 'raster'
  | 'pointCloud'
  | 'model'
  | 'vector'
  | 'document'
  | 'imagery'
  | 'other'

/**
 * The one type-to-facet mapping, shared by backend and frontend
 * (droidmap#2842).
 *
 * Every {@link AssetType} appears exactly once; a type with no meaningful
 * facet maps to `other` rather than being omitted, so a lookup never returns
 * undefined and a newly added type is a compile error here rather than a
 * silent `other` at some call site.
 */
export const ASSET_TYPE_FACETS: Readonly<Record<AssetType, AssetFacet>> = {
  // Georeferenced and plain rasters — tile-able map layers and their
  // non-georeferenced cousins.
  [AssetType.Tiff]: 'raster',
  [AssetType.GeoTiff]: 'raster',
  [AssetType.Cog]: 'raster',
  [AssetType.Dsm]: 'raster',
  [AssetType.Dtm]: 'raster',

  // Photographs. Separate from `raster` because a photo is never a map layer,
  // even though both carry pixels and EXIF.
  [AssetType.Jpeg]: 'imagery',
  [AssetType.Png]: 'imagery',
  [AssetType.Raw]: 'imagery',

  [AssetType.Las]: 'pointCloud',
  [AssetType.Laz]: 'pointCloud',

  // The model file itself, not its textures — an ODM texture atlas is a
  // plain `.tif` and lands in `raster`.
  [AssetType.Obj]: 'model',
  [AssetType.Mtl]: 'model',
  [AssetType.Ply]: 'model',
  [AssetType.Glb]: 'model',
  [AssetType.Gltf]: 'model',

  [AssetType.Geojson]: 'vector',
  [AssetType.Gpkg]: 'vector',

  [AssetType.Pdf]: 'document',
  [AssetType.Txt]: 'document',

  // Sidecar and tabular data.
  [AssetType.Csv]: 'other',
  [AssetType.Json]: 'other',
  [AssetType.Bin]: 'other',
  [AssetType.Mrk]: 'other',
  [AssetType.Nav]: 'other',
  [AssetType.Obs]: 'other',
  [AssetType.Other]: 'other',
}

/** The facet an asset type belongs to. Total over {@link AssetType}. */
export const facetOf = (assetType: AssetType): AssetFacet =>
  ASSET_TYPE_FACETS[assetType] ?? 'other'

/** Every asset type in one facet. */
/**
 * Types the UI groups under `data`: point clouds and their sidecar metadata.
 *
 * Stays an explicit list rather than a facet lookup, because it spans three
 * facets — `pointCloud` (LAS/LAZ), `vector` (GeoJSON/GPKG) and two `other`
 * members (JSON/CSV). It is a presentation grouping, not a content kind.
 */
const CATEGORY_DATA_TYPES: readonly AssetType[] = [
  AssetType.Las,
  AssetType.Laz,
  AssetType.Geojson,
  AssetType.Json,
  AssetType.Csv,
  AssetType.Gpkg,
]

/**
 * Which category an asset falls into, given the set that holds it
 * (droidmap#2887).
 *
 * **Not a pure type lookup, which is why it takes the set type.** A JPEG in an
 * `odm_outputs` set is a texture atlas and belongs with the 3D model; the same
 * JPEG in a `drone_images` set is source imagery. Any attempt to precompute
 * this per type alone gets one of those two wrong.
 *
 * Lives here, beside {@link ASSET_TYPE_FACETS}, because both the web app and
 * the asset-service need it: the frontend renders the sections and the backend
 * filters and counts by them. It was frontend-only until now, which is why
 * #2773 could not page a section and #2774 walked a whole set to find rasters.
 */
export const assetCategory = (
  assetType: AssetType,
  assetSetType: AssetSetType,
): AssetCategoryKey => {
  const facet = facetOf(assetType)
  if (facet === 'raster') return 'map'
  if (facet === 'model') return 'model'
  if (facet === 'imagery') {
    return assetSetType === AssetSetType.OdmOutputs ? 'model' : 'imagery'
  }
  if (CATEGORY_DATA_TYPES.includes(assetType)) return 'data'
  if (assetType === AssetType.Pdf) return 'documents'
  return 'other'
}

/**
 * Every asset type that falls into `category` for a set of `assetSetType`.
 *
 * The inverse of {@link assetCategory}, derived from it rather than listed
 * again, so the filter a query applies and the category a row renders under
 * cannot disagree.
 */
export const assetTypesInCategory = (
  category: AssetCategoryKey,
  assetSetType: AssetSetType,
): AssetType[] =>
  Object.keys(ASSET_TYPE_FACETS).filter(
    (type) => assetCategory(type as AssetType, assetSetType) === category,
  ) as AssetType[]

export const assetTypesInFacet = (facet: AssetFacet): AssetType[] =>
  Object.entries(ASSET_TYPE_FACETS)
    .filter(([, f]) => f === facet)
    .map(([type]) => type as AssetType)

/**
 * Asset types that are rasters: they carry image metadata (dimensions, EXIF)
 * and a thumbnail can be rendered for them.
 *
 * Single source of truth for every stage of the pipeline — the mapping-job
 * container, the asset-service upload path, and the frontend card. Three
 * independent copies of this list used to exist, and `Dsm`/`Dtm` were absent
 * from all three, so elevation rasters could not receive a thumbnail from any
 * path (droidmap#2495).
 *
 * Note that ODM writes its DEMs as plain `.tif`, which register as
 * {@link AssetType.Cog}; the explicit `Dsm`/`Dtm` types only appear for files
 * named with those extensions. Rendering therefore keys off the raster's pixel
 * format rather than its asset type — see `@droidmap/shared-thumbnails`.
 */
export const RASTER_ASSET_TYPES: readonly AssetType[] = [
  AssetType.Jpeg,
  AssetType.Png,
  AssetType.Tiff,
  AssetType.GeoTiff,
  AssetType.Cog,
  AssetType.Raw,
  AssetType.Dsm,
  AssetType.Dtm,
]

/**
 * Type guard to check if an asset should have AssetImageMetadata based on its type
 */
export function isImageAsset(asset: Asset | AssetWithPresignedUrl): asset is (
  Asset | AssetWithPresignedUrl
) & {
  assetMetadata?: AssetMetadataImage
} {
  return RASTER_ASSET_TYPES.includes(asset.assetType)
}

/**
 * Check if an asset type supports thumbnail generation.
 */
export const supportsThumbnailGeneration = (assetType: AssetType): boolean =>
  RASTER_ASSET_TYPES.includes(assetType)

/**
 * Type guard to check if an asset should have AssetLidarMetadata based on its type
 */
export function isLidarAsset(asset: Asset | AssetWithPresignedUrl): asset is (
  Asset | AssetWithPresignedUrl
) & {
  assetMetadata?: AssetMetadataLidar
} {
  return [AssetType.Las, AssetType.Laz].includes(asset.assetType)
}

/**
 * Calculate summary stats for an asset set used to hold images.
 *
 * These stats can be used to estimate what the compute requirements
 * for a photogrammetry job might be.
 *
 * @returns The stats for the image asset sets
 */
export const calculateImageAssetSetMetadata = async (
  inputAssetSets: string[],
  assetServiceClient: AssetServiceClient,
  metadata?: DroidMapMetadata,
): Promise<AssetSetMetadataImages> => {
  // Look up the asset sets
  const assetSets: AssetSet[] = []
  for (const inputAssetSetId of inputAssetSets) {
    const assetSet = await assetServiceClient.getAssetSet(
      {
        assetSetId: inputAssetSetId,
      },
      metadata,
    )
    if (!assetSet) {
      throw new DroidMapError('Asset set not found', {
        context: { inputAssetSetId },
      })
    }
    assetSets.push(assetSet)
  }

  // We are only interested in AssetSets of type DRONE_IMAGES
  const droneImageSets = assetSets.filter(
    (assetSet) => assetSet.assetSetType === AssetSetType.DroneImages,
  )
  if (droneImageSets.length === 0) {
    throw new DroidMapError('No drone image sets found')
  }

  // Iterate over the assets in the asset sets and calculate the stats
  let totalFiles = 0
  let totalPixelCount = 0
  let totalSizeInBytes = 0
  let uniform = true
  let imageWidth = 0
  let imageHeight = 0
  let imagePixels = 0

  for (const droneImageSet of droneImageSets) {
    let nextToken: string | undefined = undefined
    do {
      const { items: assets, next } =
        await assetServiceClient.listAssetsByAssetSet(
          {
            assetSetId: droneImageSet.id,
            next: nextToken,
          },
          metadata,
        )

      // Filter out macOS metadata files before processing
      const validAssets = assets.filter(
        (asset) => !isMacOSMetadataFile(asset.name),
      )

      validAssets.filter(isImageAsset).forEach((asset) => {
        const { size, width, height } = validateImage(asset)
        const pixels = width * height

        totalFiles++
        totalSizeInBytes += size
        totalPixelCount += pixels

        if (uniform) {
          if (imageWidth === 0 && imageHeight === 0) {
            imageWidth = width
            imageHeight = height
          } else if (imageWidth !== width || imageHeight !== height) {
            uniform = false
          }
        }

        if (width > imageWidth) {
          imageWidth = width
        }

        if (height > imageHeight) {
          imageHeight = height
        }

        if (imagePixels === 0) {
          imagePixels = pixels
        } else if (pixels > imagePixels) {
          imagePixels = pixels
        }
      })

      nextToken = next
    } while (nextToken)
  }

  const pixelsToMegapixels = (pixels: number): number => {
    return pixels / 1000000
  }

  return {
    totalFiles,
    totalPixelsInMegapixels: pixelsToMegapixels(totalPixelCount),
    totalSizeInBytes,
    uniform,
    imageDimensions: {
      width: imageWidth,
      height: imageHeight,
    },
    imageMegapixels: pixelsToMegapixels(imagePixels),
  }
}

/**
 * The size of a photogrammetry job's input imagery on the two axes that
 * govern its runtime and its cost.
 */
export type InputImagerySize = {
  /** Total gigapixels across the drone-image input sets. */
  gigapixels: number
  /**
   * Total image files across those sets, or `undefined` when the cached
   * asset-set metadata doesn't carry a file count. Distinct from `0`, which
   * means "sized, and there genuinely are no images".
   */
  imageCount?: number
  /**
   * How many of the input sets were drone-image sets.
   *
   * `0` means nothing here is imagery at all, which is a different failure
   * from "imagery that measures zero" and the two must not be conflated: a
   * photogrammetry job given no image set has to fail loudly rather than be
   * sized at the smallest tier and die later inside ODM.
   * {@link calculateImageAssetSetMetadata} expressed that by throwing; this
   * function reports it instead, so a caller that only wants a size is not
   * forced to catch.
   */
  droneImageSetCount: number
}

/**
 * Size the drone-image input asset sets on both axes: total gigapixels (the
 * cost-driving axis) and image count.
 *
 * Prefers the asset set's `assetSetMetadata` totals, which makes the estimate
 * O(number of asset sets) — one cheap `getAssetSet` per set — instead of
 * paging every asset. Only when a drone-image set is missing its pixel total
 * does it fall back to {@link calculateImageAssetSetMetadata}, which pages the
 * assets to recompute both figures.
 *
 * That fast path was dead until droidmap#2769. The totals were supposed to
 * arrive by client PATCH and no client ever sent them, so the field was NULL
 * for every asset set in the estate and *every* call took the paging fallback
 * — the opposite of what this comment used to claim. They are now derived
 * server-side from the set's raster facet, maintained as assets are added and
 * removed, so they are present regardless of which client did the upload.
 *
 * Both axes are wanted together because runtime is not a smooth function of
 * pixels: `imageCount / gigapixels` is a proxy for image density, and density
 * is what decides which matcher regime a job lands in (droidmap#2476). The
 * pixel total alone prices the job; the pair is what makes two runtimes
 * comparable.
 *
 * Intended for coarse sizing (the quota gate's job-cost estimate, #1711, and
 * the runtime snapshot recorded on a task), where a slightly stale cached
 * total is acceptable; authoritative per-asset stats still come from
 * {@link calculateImageAssetSetMetadata}. Returns zero size when no input set
 * is a drone-image set.
 */
export const estimateInputImagery = async (
  inputAssetSets: string[],
  assetServiceClient: AssetServiceClient,
  metadata?: DroidMapMetadata,
): Promise<InputImagerySize> => {
  const assetSets: AssetSet[] = []
  for (const inputAssetSetId of inputAssetSets) {
    const assetSet = await assetServiceClient.getAssetSet(
      { assetSetId: inputAssetSetId },
      metadata,
    )
    if (assetSet) {
      assetSets.push(assetSet)
    }
  }

  const droneImageSets = assetSets.filter(
    (assetSet) => assetSet.assetSetType === AssetSetType.DroneImages,
  )
  if (droneImageSets.length === 0) {
    return { gigapixels: 0, imageCount: 0, droneImageSetCount: 0 }
  }

  // Fast path: every drone-image set carries a cached pixel total, so we can
  // sum them without paging any assets.
  const everySetHasCachedTotal = droneImageSets.every(
    (assetSet) => !!assetSet.assetSetMetadata?.totalPixelsInMegapixels,
  )
  if (everySetHasCachedTotal) {
    const totalMegapixels = droneImageSets.reduce(
      (sum, assetSet) =>
        sum + (assetSet.assetSetMetadata?.totalPixelsInMegapixels ?? 0),
      0,
    )
    // `totalFiles` is optional on the cached metadata and the pixel total is
    // what the caller is really here for, so a set that predates it degrades
    // the count to "unknown" rather than dragging the whole sizing onto the
    // paging fallback — or, worse, reporting a silently short count.
    const everySetHasCachedFileCount = droneImageSets.every(
      (assetSet) => assetSet.assetSetMetadata?.totalFiles !== undefined,
    )
    return {
      gigapixels: megapixelsToGigapixels(totalMegapixels),
      droneImageSetCount: droneImageSets.length,
      ...(everySetHasCachedFileCount && {
        imageCount: droneImageSets.reduce(
          (sum, assetSet) => sum + (assetSet.assetSetMetadata?.totalFiles ?? 0),
          0,
        ),
      }),
    }
  }

  // Fallback: at least one set lacks a cached total — recompute by paging
  // assets (the authoritative, more expensive path).
  const stats = await calculateImageAssetSetMetadata(
    inputAssetSets,
    assetServiceClient,
    metadata,
  )
  return {
    gigapixels: megapixelsToGigapixels(stats.totalPixelsInMegapixels),
    droneImageSetCount: droneImageSets.length,
    ...(stats.totalFiles !== undefined && { imageCount: stats.totalFiles }),
  }
}

/**
 * Estimate the total gigapixels of the drone-image input asset sets — the
 * cost-driving size axis for a photogrammetry mapping job. Thin projection of
 * {@link estimateInputImagery}; see there for the sizing strategy.
 */
export const estimateInputGigapixels = async (
  inputAssetSets: string[],
  assetServiceClient: AssetServiceClient,
  metadata?: DroidMapMetadata,
): Promise<number> =>
  (await estimateInputImagery(inputAssetSets, assetServiceClient, metadata))
    .gigapixels

/**
 * Convert megapixels to gigapixels — the sizing unit the mapping-job cost
 * estimator works in. Exported so callers sizing from other megapixel
 * sources (e.g. client-measured image stats, #2251) stay mechanically
 * identical to the asset-set sizing above rather than coincidentally so.
 */
export const megapixelsToGigapixels = (megapixels: number): number =>
  megapixels / 1000

/**
 * Validate an asset is an image asset and has the necessary metadata.
 *
 * @param asset - The asset to validate.
 * @returns The dimensions of the image.
 */
/**
 * A whole number of pixels within the bound the API now enforces
 * (droidmap#2843). `Number.isInteger` also rejects `NaN` and both
 * infinities, which JSON cannot carry but JavaScript can produce.
 */
const isUsableImageDimension = (value: number): boolean =>
  Number.isInteger(value) && value >= 1 && value <= MAX_IMAGE_DIMENSION

export const validateImage = (
  asset: AssetWithPresignedUrl,
): { size: number; width: number; height: number } => {
  if (!asset.uploadConfirmed) {
    throw new DroidMapError('Asset has not been uploaded.', {
      context: { asset },
    })
  }

  if (!asset.size) {
    throw new DroidMapError('Asset does not have size set.', {
      context: { asset },
    })
  }

  if (!asset.assetMetadata) {
    throw new DroidMapError('Asset does not have metadata set.', {
      context: { asset },
    })
  }

  if (isImageAsset(asset)) {
    const dimensions = asset.assetMetadata?.dimensions

    if (!dimensions) {
      throw new DroidMapError(
        'Asset does not have dimensions set in metadata.',
        {
          context: { asset },
        },
      )
    }

    if (!dimensions.width || !dimensions.height) {
      throw new DroidMapError('Asset does not have height or width set.', {
        context: { asset },
      })
    }

    // The caller multiplies these, and sums the product across a whole asset
    // set. `1e308` was a legitimate request before droidmap#2843 bounded the
    // API edge, so a row stored before that can still hold one — and an
    // unbounded multiply here turns a single bad row into an `Infinity`
    // gigapixel total, which prices a job and gates a quota.
    //
    // Refused rather than clamped, matching the edge: a wrong number that
    // looks plausible is worse than a call that fails and says why.
    if (
      !isUsableImageDimension(dimensions.width) ||
      !isUsableImageDimension(dimensions.height)
    ) {
      throw new DroidMapError(
        `Asset dimensions are outside the accepted range (1-${MAX_IMAGE_DIMENSION} px per side).`,
        { context: { asset } },
      )
    }

    return {
      size: asset.size,
      width: dimensions.width,
      height: dimensions.height,
    }
  } else {
    throw new DroidMapError(
      `Asset is not an image asset (type: ${asset.assetType}).`,
      {
        context: { asset },
      },
    )
  }
}

/**
 * Check if a StorageAndAssetCountDelta contains any non-zero values
 *
 * @param storageAndAssetCountDelta - The delta to check
 * @returns true if any delta value is non-zero, false otherwise
 */
export const hasNonZeroDeltas = (
  storageAndAssetCountDelta: StorageAndAssetCountDelta,
): boolean => {
  return (
    storageAndAssetCountDelta.assetCountDelta !== 0 ||
    storageAndAssetCountDelta.storageSummaryDelta.standard !== 0 ||
    storageAndAssetCountDelta.storageSummaryDelta.standardIA !== 0 ||
    storageAndAssetCountDelta.storageSummaryDelta.oneZoneIA !== 0 ||
    storageAndAssetCountDelta.storageSummaryDelta.glacierIR !== 0 ||
    storageAndAssetCountDelta.storageSummaryDelta.glacierFlex !== 0 ||
    storageAndAssetCountDelta.storageSummaryDelta.glacierDA !== 0
  )
}

/**
 * Create DynamoDB update operations object from a StorageAndAssetCountDelta
 *
 * @param storageAndAssetCountDelta - The delta containing storage summary and asset count changes
 * @param updatedBy - The user ID who made the update
 * @returns DynamoDB update operations object with only non-zero delta values
 */
export const createUpdateOperationsFromDelta = (
  storageAndAssetCountDelta: StorageAndAssetCountDelta,
  updatedBy: string,
): { set?: Record<string, any>; add?: Record<string, any> } => {
  const updateOperations: {
    set?: Record<string, any>
    add?: Record<string, any>
  } = {
    set: {
      updatedBy,
    },
  }

  // Add storage summary delta operations only for non-zero values
  if (storageAndAssetCountDelta.storageSummaryDelta) {
    const storageDeltas: Record<string, number> = {}

    if (storageAndAssetCountDelta.storageSummaryDelta.standard !== 0) {
      storageDeltas['storageSummary.standard'] =
        storageAndAssetCountDelta.storageSummaryDelta.standard
    }
    if (storageAndAssetCountDelta.storageSummaryDelta.standardIA !== 0) {
      storageDeltas['storageSummary.standardIA'] =
        storageAndAssetCountDelta.storageSummaryDelta.standardIA
    }
    if (storageAndAssetCountDelta.storageSummaryDelta.oneZoneIA !== 0) {
      storageDeltas['storageSummary.oneZoneIA'] =
        storageAndAssetCountDelta.storageSummaryDelta.oneZoneIA
    }
    if (storageAndAssetCountDelta.storageSummaryDelta.glacierIR !== 0) {
      storageDeltas['storageSummary.glacierIR'] =
        storageAndAssetCountDelta.storageSummaryDelta.glacierIR
    }
    if (storageAndAssetCountDelta.storageSummaryDelta.glacierFlex !== 0) {
      storageDeltas['storageSummary.glacierFlex'] =
        storageAndAssetCountDelta.storageSummaryDelta.glacierFlex
    }
    if (storageAndAssetCountDelta.storageSummaryDelta.glacierDA !== 0) {
      storageDeltas['storageSummary.glacierDA'] =
        storageAndAssetCountDelta.storageSummaryDelta.glacierDA
    }

    if (Object.keys(storageDeltas).length > 0) {
      updateOperations.add = storageDeltas
    }
  }

  // Add asset count delta operation only if non-zero
  if (storageAndAssetCountDelta.assetCountDelta !== 0) {
    updateOperations.add = {
      ...updateOperations.add,
      assetCount: storageAndAssetCountDelta.assetCountDelta,
    }
  }

  return updateOperations
}

/**
 * Determines the asset type from a File object based on its extension and content analysis.
 * This function is called from the frontend.
 * @param file - The File object to analyze
 * @returns The appropriate AssetType
 */
export async function calculateAssetTypeFromFile(
  file: File,
): Promise<AssetType> {
  const fileName = file.name.toLowerCase()
  let extension = ''
  if (fileName.includes('.')) {
    const ext = fileName.split('.').pop()
    extension = ext ? '.' + ext : ''
  }

  // Extension to AssetType mapping (same as calculateAssetType)
  let assetType = calculateAssetTypeFromExtension(extension)

  // We only need to read the first 1000 bytes for TIFF type detection
  if (assetType === AssetType.Tiff) {
    try {
      // Read only the first 1000 bytes instead of the entire file
      const TIFF_DETECTION_BYTES = 1000
      const bytesToRead = Math.min(TIFF_DETECTION_BYTES, file.size)
      const fileSlice = file.slice(0, bytesToRead)
      const arrayBuffer = await fileSlice.arrayBuffer()
      assetType = refineTiffAssetType(arrayBuffer, file.name)
    } catch (error) {
      const err = ensureError(error)
      throw new DroidMapError('Error reading file for TIFF analysis', {
        cause: err,
        context: { fileName: file.name },
      })
    }
  }

  return assetType
}

export const calculateAssetTypeFromExtension = (
  extension: string,
): AssetType => {
  extension = extension.toLowerCase()

  const extensionToAssetType: Record<string, AssetType> = {
    '.bin': AssetType.Bin,
    '.cog': AssetType.Cog,
    '.csv': AssetType.Csv,
    '.dsm': AssetType.Dsm,
    '.dtm': AssetType.Dtm,
    '.geotiff': AssetType.GeoTiff,
    '.geojson': AssetType.Geojson,
    '.glb': AssetType.Glb,
    '.gltf': AssetType.Gltf,
    '.gpkg': AssetType.Gpkg,
    '.jpeg': AssetType.Jpeg,
    '.jpg': AssetType.Jpeg,
    '.json': AssetType.Json,
    '.laz': AssetType.Laz,
    '.las': AssetType.Las,
    '.mrk': AssetType.Mrk,
    '.mtl': AssetType.Mtl,
    '.nav': AssetType.Nav,
    '.obj': AssetType.Obj,
    '.obs': AssetType.Obs,
    '.pdf': AssetType.Pdf,
    '.ply': AssetType.Ply,
    '.png': AssetType.Png,
    '.raw': AssetType.Raw,
    '.tif': AssetType.Tiff,
    '.tiff': AssetType.Tiff,
    '.txt': AssetType.Txt,
  }

  let assetType = AssetType.Other

  if (extension in extensionToAssetType) {
    assetType = extensionToAssetType[extension]
  }
  return assetType
}

/**
 * Refine a TIFF asset type to COG or GeoTIFF based on file content analysis.
 *
 * @param fileData - The file data as ArrayBuffer, Uint8Array, or Buffer
 * @param fileName - The name of the file for logging purposes
 * @returns The refined asset type (COG, GeoTIFF, or original TIFF if refinement fails)
 */
export const refineTiffAssetType = (
  fileData: ArrayBuffer | Uint8Array | Buffer,
  fileName: string,
): AssetType => {
  try {
    const tiffType = getTiffType(fileData)
    if (tiffType === 'cog') {
      return AssetType.Cog
    } else if (tiffType === 'geotiff') {
      return AssetType.GeoTiff
    }
  } catch (error) {
    const err = ensureError(error)
    throw new DroidMapError('Error reading file for TIFF analysis', {
      cause: err,
      context: { fileName },
    })
  }

  return AssetType.Tiff
}

/**
 * Build a DynamoDB WHERE clause and substitutions for filtering by a list of IDs.
 * Handles the DynamoDB IN operator's 100-value limit by creating multiple OR'd IN clauses.
 *
 * **Important Limits:**
 * - DynamoDB IN operator: max 100 values per clause
 * - DynamoDB expression size: max 4KB (4096 characters)
 * - For 26-char ULIDs: ~140 ULIDs maximum (~1.4 chunks)
 * - For longer IDs or many chunks, consider alternative query patterns
 *
 * **Note:** For users with >150 permissions, see issue #1229 for long-term solutions
 * (denormalized GSI, access table, or fetch-and-filter pattern).
 *
 * @see https://github.com/droidmap/droidmap/issues/1229
 *
 * @param fieldName - The field name to filter on (e.g., 'id')
 * @param values - Array of values to filter by
 * @returns Object containing the WHERE clause string and substitutions object
 * @throws {Error} If the expression would exceed DynamoDB's 4KB limit
 *
 * @example
 * // For 50 values
 * buildInClauseQuery('id', ['1', '2', ..., '50'])
 * // Returns: { whereClause: '(${id} IN (@{...chunk0}))', substitutions: { chunk0: ['1', '2', ..., '50'] } }
 *
 * @example
 * // For 250 values - will throw error (exceeds 4KB limit)
 * buildInClauseQuery('id', ['1', '2', ..., '250'])
 * // Throws: Error('Expression size would exceed DynamoDB 4KB limit')
 */
export const buildInClauseQuery = (
  fieldName: string,
  values: string[],
): {
  whereClause: string
  substitutions: Record<string, string[]>
} => {
  // DynamoDB limits:
  // 1. IN operator: max 100 values per clause
  // 2. Expression size: max 4KB (4096 characters)
  const MAX_IN_CLAUSE_SIZE = 100

  // For 26-char ULIDs, we can fit ~140 in 4KB
  // Conservative estimate: warn at 140, fail at 150
  const RECOMMENDED_MAX_VALUES = 140
  const ABSOLUTE_MAX_VALUES = 150

  if (values.length > ABSOLUTE_MAX_VALUES) {
    throw new Error(
      `Too many values (${values.length}) for DynamoDB IN clause query. ` +
        `Maximum supported is ~${ABSOLUTE_MAX_VALUES} due to 4KB expression size limit.`,
    )
  }

  if (values.length > RECOMMENDED_MAX_VALUES) {
    console.warn(
      `Warning: ${values.length} values approaching DynamoDB 4KB expression limit. ` +
        `Recommended maximum is ${RECOMMENDED_MAX_VALUES} for 26-char IDs.`,
    )
  }

  const chunkCount = Math.ceil(values.length / MAX_IN_CLAUSE_SIZE)
  const chunks = Array.from({ length: chunkCount }, (_, i) =>
    values.slice(i * MAX_IN_CLAUSE_SIZE, (i + 1) * MAX_IN_CLAUSE_SIZE),
  )

  const { whereClauses, substitutions } = chunks.reduce<{
    whereClauses: string[]
    substitutions: Record<string, string[]>
  }>(
    (acc, chunk, index) => {
      const chunkKey = `chunk${index}`
      acc.whereClauses.push(`(\${${fieldName}} IN (@{...${chunkKey}}))`)
      acc.substitutions[chunkKey] = chunk
      return acc
    },
    { whereClauses: [], substitutions: {} },
  )

  return {
    whereClause: whereClauses.join(' OR '),
    substitutions,
  }
}
